import { useDispatch, useSelector } from "react-redux";
import { getImageUrl } from "../../../Utils/helpers";
import * as P from "../style";
import * as C from "../../../common-styles";
import { AppDispatch, RootState } from "../../../Redux/store";
import { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import { UserProfile } from "../../../Config/interface.config";
import { getUserDetails, updateProfile, setProfileImage } from "../../../Redux/slices/userSlice";
import type { UploadProps } from "antd";
import { Input, message, Upload } from "antd";
import DefaultUserImg from "../../../Assets/Images/user-profile.png";
import * as API from "../../../API/path";
import { isCreatorRole } from "../../../Config/common.config";
import * as D from "../../../Components/UserDetails/style";
import { ReactComponent as PlusIcon } from "./plus-icon.svg";
import { ReactComponent as GalleryIcon } from "./gallery-icon.svg";
import WhiteTick from "../../../Assets/Icons/WhiteTick";
import IntroFrame from "../../../Assets/Images/intro-post-frame.png";
import CreatePostModal from "../../../Modals/CreatePostModal";
import DeletePostModal from "../../../Modals/DeleteConfirmationModal";
import PostHeader from "../../../Components/Post/PostHeader";
import { Capitalize } from "../../../Utils";
import PostSlider from "../../../Components/Post/PostSlider";
import { postDelete } from "../../../Redux/slices/postSlice";
import toast from "react-hot-toast";
import * as Yup from "yup";

const INDIA_CODE = "+91";
const INTERNATIONAL_CODE = "INTL";

const normalizeDigits = (value?: string) => String(value ?? "").replace(/\D/g, "");

const EditProfile = () => {
  const dispatch: AppDispatch = useDispatch();
  const user = useSelector((state: RootState) => state.user);
  const { socialLinks, fullname, totalPost = 0 } = user;
  const [createPostModal, setCreatePostModal] = useState(false);
  const [deletePostModal, setDeletePostModal] = useState(false);
  const [editPostModal, setEditPostModal] = useState(false);
  const approvedCreator = isCreatorRole(user.role) && user.isApproved === true;

  const [profileData, setProfileData] = useState<UserProfile>({
    fullname: "",
    email: "",
    username: "",
    bio: "",
    countryCode: INDIA_CODE,
    contactNumber: "",
  });

  const validationSchema = Yup.object().shape({
    fullname: Yup.string().required("Full name is required"),
    email: Yup.string().email("Invalid email").required("Email is required"),
    username: Yup.string()
      .matches(/^\S*$/, "Username must be without spaces")
      .required("Username is required"),
    bio: Yup.string(),
    countryCode: Yup.string().oneOf([INDIA_CODE, INTERNATIONAL_CODE], "Contact type is required"),
    contactNumber: Yup.string().when("countryCode", {
      is: INDIA_CODE,
      then: (schema) =>
        schema.test("india-mobile", "Enter a valid 10 digit Indian mobile number", (value) => {
          if (!value) return true;
          const digits = normalizeDigits(value);
          return /^[6-9]\d{9}$/.test(digits);
        }),
      otherwise: (schema) =>
        schema.test("international-contact", "Enter a valid contact number", (value) => {
          if (!value) return true;
          const trimmed = String(value).trim();
          return trimmed.length >= 8 && trimmed.length <= 20;
        }),
    }),
  });

  const { TextArea } = Input;

  useEffect(() => {
    if (!user.id || !user.email || !user.fullname) return;

    const savedContactNumber = normalizeDigits(user.contactNumber);
    const parsedContact =
      savedContactNumber.startsWith("91") && savedContactNumber.length === 12
        ? { countryCode: INDIA_CODE, contactNumber: savedContactNumber.slice(2) }
        : savedContactNumber
          ? { countryCode: INTERNATIONAL_CODE, contactNumber: savedContactNumber }
          : { countryCode: INDIA_CODE, contactNumber: "" };

    setProfileData((previousProfileData) => ({
      ...previousProfileData,
      email: user.email,
      fullname: user.fullname,
      id: user.id,
      username: user.username ?? "",
      bio: user.bio,
      ...parsedContact,
    }));
  }, [user]);

  useEffect(() => {
    if (!user.id || !user.email || !user.fullname) dispatch(getUserDetails());
  }, [dispatch, user.email, user.fullname, user.id]);

  const updateProfileHandler = (updatedUser: UserProfile) => {
    if (!approvedCreator) {
      delete updatedUser.username;
      delete updatedUser.contactNumber;
      delete updatedUser.countryCode;
    } else if (updatedUser.contactNumber) {
      updatedUser.contactNumber =
        updatedUser.countryCode === INDIA_CODE
          ? `91${normalizeDigits(updatedUser.contactNumber)}`
          : String(updatedUser.contactNumber).trim();
      delete updatedUser.countryCode;
    } else {
      delete updatedUser.contactNumber;
      delete updatedUser.countryCode;
    }
    if (!user.loading) dispatch(updateProfile(updatedUser));
  };

  const imageFileTypes = ["image/jpeg", "image/png", "image/webp", "image/gif"];
  const props: UploadProps = {
    name: "file",
    action: `${process.env.REACT_APP_API_ENDPOINT}${API.fileUpload.url}`,
    data: { uploadType: 'profile-picture' },
    headers: {
      authorization: "authorization-text",
    },
    showUploadList: false,
    method: "POST",
    beforeUpload: (file) => {
      if (!imageFileTypes.includes(file.type)) {
        message.error("You can only upload image files (JPG, PNG, WEBP, GIF)");
        return Upload.LIST_IGNORE; // stops upload
      }
      return true; // allow upload
    },
    onChange(info) {
      if (info.file.status !== "uploading") {
        message.loading("Uploading profile picture...");
      }
      if (info.file.status === "done") {
        message.destroy();
        if (typeof info.file.response.name === 'string') {
          const { name: fileName } = info.file.response;
          const fileURL = `${process.env.REACT_APP_API_ENDPOINT?.replace(
            '/v1',
            ''
          )}/uploads/profile-pictures/${fileName}`;
          dispatch(setProfileImage({ imageUrl: fileURL }));
          dispatch(updateProfile({ image: info.file.response._id }));
        }
      } else if (info.file.status === "error") {
        message.error(`Error, try again later...`);
      }
    },
  };

  const propsBackgroundImage: UploadProps = {
    name: "file",
    action: `${process.env.REACT_APP_API_ENDPOINT}${API.fileUpload.url}`,
    data: { uploadType: 'background-image' },
    headers: {
      authorization: "authorization-text",
    },
    showUploadList: false,
    method: "POST",
    onChange(info) {
      if (info.file.status !== "uploading") {
        message.loading("Uploading background image...");
      }
      if (info.file.status === "done") {
        message.destroy();
        if (typeof info.file.response.name === 'string') {
          const { name: fileName } = info.file.response;
          const fileURL = `${process.env.REACT_APP_API_ENDPOINT?.replace(
            '/v1',
            ''
          )}/uploads/background-images/${fileName}`;
          dispatch(setProfileImage({ backgroundImageUrl: fileURL }));
          dispatch(updateProfile({ backgroundImage: info.file.response._id }));
        }
      } else if (info.file.status === "error") {
        message.error(`Error, try again later...`);
      }
    },
  };

  const toggleCreatePostModal = () => setCreatePostModal(!createPostModal);

  const toggleDeletePostModal = () => setDeletePostModal(!deletePostModal);

  const toggleEditPostModal = () => setEditPostModal(!editPostModal);

  const onConfirmDeleteIntroPost = async (isConfirm: boolean) => {
    if (!isConfirm) {
      toggleDeletePostModal();
    }

    user?.introPost?._id &&
      (await dispatch(postDelete({ postId: user.introPost._id })).then(() => {
        toast.success("Intro post deleted successfuly.");
        setTimeout(() => {
          toggleDeletePostModal();
          dispatch(getUserDetails());
        }, 2000);
      }));
  };

  useEffect(() => {
    // console.log("from use effect", user);
  }, [user]);

  return (
    <>
      <P.ProfileEditWrapper>
        <D.ProfileHeaderContainer>
          <div className="section-one">
            {user?.backgroundImage?.fileURL && (
              <D.ProfileBackgroundImage
                src={getImageUrl(user?.backgroundImage?.fileURL ?? "")}
              />
            )}

            {approvedCreator && (
              <Upload
                showUploadList={false}
                className="profile-img-upload flex items-center justify-center"
                {...propsBackgroundImage}
              >
                <C.CommonButton
                  variant="dark"
                  className="flex items-center gap-3"
                >
                  Add a cover
                  <PlusIcon />
                </C.CommonButton>
              </Upload>
            )}
          </div>

          <div className="section-two">
            <div className="flex items-center justify-between mt-3 gap-3">
              <div className="flex items-center gap-4 min-w-0">
                <div className="relative cursor-pointer profile-img-box bottom-[75px] rounded-full">
                  <D.ProfileImage
                    src={getImageUrl(user?.image?.fileURL ?? "") || DefaultUserImg}
                    alt="User"
                  />
                  <Upload
                    showUploadList={false}
                    className="profile-img-upload flex items-center justify-center"
                    {...props}
                  >
                    <p className="text-white">Change</p>
                  </Upload>
                </div>
                <div className="mb-1 min-w-0">
                  <div className="flex gap-2 sm:gap-4 items-center min-w-0">
                    <h1 className="text-white font-medium font-inter text-xl sm:text-3xl truncate">
                      {fullname}
                    </h1>
                    {approvedCreator && <WhiteTick key={"white"} />}
                  </div>
                  {socialLinks?.username && (
                    <p className="text-white opacity-[67%] font-inter font-light">
                      @{socialLinks?.username}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-5 shrink-0">
                {approvedCreator && (
                  <div className="flex gap-2">
                    <GalleryIcon />
                    <span className="text-white count-span">{totalPost}</span>
                  </div>
                )}

                {/* <div className="flex gap-2">
                <LockIcon />
                <span className="text-white count-span">0</span>
              </div> */}
              </div>
            </div>
          </div>
        </D.ProfileHeaderContainer>
        <P.FormWrapper>
          <Formik
            enableReinitialize
            initialValues={profileData}
            onSubmit={updateProfileHandler}
            validationSchema={validationSchema}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              dirty,
              submitForm,
              setFieldValue,
            }) => (
              <Form>
                <div className="profile-edit-form">
                  <div className="my-6">
                    <div className="mb-8">
                      <p className="text-white mb-0.5">Full Name </p>
                      <div className="flex-1">
                        <C.CommonInput
                          placeholder="Full Name"
                          name="fullname"
                          value={values.fullname}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>

                    <div className="mb-8">
                      <p className="text-white mb-0.5">Email Address </p>
                      <div className="flex-1">
                        <C.CommonInput
                          disabled
                          placeholder="Email"
                          name="email"
                          value={values.email}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      </div>
                    </div>
                    {approvedCreator && (
                      <>
                        <div className="mb-8">
                          <p className="text-white mb-0.5">Description</p>
                          <TextArea
                            className="bg-black border-none border-b border-b-white"
                            rows={3}
                            placeholder="Write bio here..."
                            name="bio"
                            value={values.bio}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        </div>

                        <div className="mb-8">
                          <p className="text-white mb-0.5">Username </p>
                          <div className="flex-1">
                            <C.CommonInput
                              placeholder="Username"
                              name="username"
                              value={values.username}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors.username && touched.username && (
                              <p className="text-red-500 text-sm mt-1">
                                {errors.username}
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="mb-8">
                          <p className="text-white mb-0.5">Contact Number</p>
                          <P.ContactRow>
                            <P.ContactSelector
                              name="countryCode"
                              aria-label="Contact number type"
                              value={values.countryCode}
                              onChange={(event) => {
                                setFieldValue("countryCode", event.target.value);
                                setFieldValue("contactNumber", "");
                              }}
                              onBlur={handleBlur}
                            >
                              <option value={INDIA_CODE}>+91</option>
                              <option value={INTERNATIONAL_CODE}>INTL</option>
                            </P.ContactSelector>
                            <C.CommonInput
                              type="tel"
                              placeholder={values.countryCode === INDIA_CODE ? "10 digit mobile number" : "Full number with country code"}
                              name="contactNumber"
                              value={values.contactNumber}
                              inputMode={values.countryCode === INDIA_CODE ? "numeric" : undefined}
                              pattern={values.countryCode === INDIA_CODE ? "[0-9]*" : undefined}
                              maxLength={values.countryCode === INDIA_CODE ? 10 : undefined}
                              onChange={(event) => {
                                const nextValue =
                                  values.countryCode === INDIA_CODE
                                    ? normalizeDigits(event.target.value).slice(0, 10)
                                    : event.target.value;
                                setFieldValue("contactNumber", nextValue);
                              }}
                              onBlur={handleBlur}
                            />
                          </P.ContactRow>
                          {((errors.countryCode && touched.countryCode) ||
                            (errors.contactNumber && touched.contactNumber)) && (
                            <p className="text-red-500 text-sm mt-1">
                              {(errors.countryCode as string) || (errors.contactNumber as string)}
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    {dirty && (
                      <p onClick={submitForm} className="update-profile-btn">
                        Save Changes
                      </p>
                    )}

                    <p className="text-white mt-1">
                      {errors.email && touched.email && errors.email}
                    </p>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </P.FormWrapper>
        {approvedCreator && (
          <>
            {user?.introPost && Object.keys(user.introPost).length > 0 ? (
              <P.AddIntroPost>
                <div className="flex items-center justify-between">
                  <div className="mb-6">
                    <h2 className="text-white font-medium font-inter text-md sm:text-3xl">
                      Edit Intro post
                    </h2>
                  </div>
                  <div className="mb-6">
                    <button
                      className="bg-transparent py-2 px-8 text-white text-lg border rounded-full border-white"
                      onClick={toggleEditPostModal}
                    >
                      Change
                    </button>
                    <button
                      className="bg-transparent ml-5 p-2 text-white text-2xl"
                      onClick={toggleDeletePostModal}
                    >
                      X
                    </button>
                  </div>
                </div>
                <div className="flex items-start text-left flex-col p-6">
                  <P.Post>
                    <PostHeader
                      post={user.introPost}
                      userImage={user.image?.fileURL}
                    />
                    <P.Description>
                      {Capitalize(user.introPost.text)}
                    </P.Description>
                    <PostSlider post={user.introPost} locked={false} />
                  </P.Post>
                </div>
              </P.AddIntroPost>
            ) : (
              <P.AddIntroPost>
                <h2 className="text-white font-medium font-inter text-md sm:text-3xl mb-6">
                  Add Intro post
                </h2>
                <div className="flex items-center text-center flex-col p-6">
                  <P.CardFrame
                    src={IntroFrame}
                    alt=""
                  />
                  <h1 className="text-white font-medium font-inter text-md sm:text-3xl my-6">
                    Create an intro post to engage your fan
                  </h1>
                  <p className="text-white font-inter font-extralight text-md sm:text-3xl">
                    Share a free post to give a sneak peak of your content.
                  </p>
                  <button
                    className="bg-transparent my-16 py-2 w-2/5 text-white text-lg border rounded-full border-white"
                    onClick={toggleCreatePostModal}
                  >
                    Create
                  </button>
                </div>
              </P.AddIntroPost>
            )}
          </>
        )}
      </P.ProfileEditWrapper>

      {/* Create Post Modal */}
      <CreatePostModal
        onHide={() => toggleCreatePostModal()}
        show={createPostModal}
        key={"create-post-modal"}
        isIntro={true}
      />

      {/* edit Post Modal */}
      <CreatePostModal
        onHide={() => toggleEditPostModal()}
        show={editPostModal}
        key={"create-post-modal"}
        isIntro={true}
        editPost={user.introPost}
      />

      {/* Delete Post Modal */}
      <DeletePostModal
        onHide={() => toggleDeletePostModal()}
        show={deletePostModal}
        setConfirmDelete={(isConfirm) => onConfirmDeleteIntroPost(isConfirm)}
      />
    </>
  );
};

export default EditProfile;
